import * as api from "@/api/williams/scopesOfWork";
import { ProjectSchema } from "../projects/ProjectSchema";
import { RegionSchema } from "@/api/williams/region";
import { ScopeOfWork, ScopeOfWorkStatus } from "@/api/williams/scopesOfWork";
import { UserSchema } from "../users";
import { WelderSchema } from "../welders/WelderSchema";
import { z } from "zod";

const REQUIRED_FIELD_ERROR = "Required Field";

export const ScopeOfWorkSchema = z
  .object({
    id: z.string(),
    number: z.string().nonempty(REQUIRED_FIELD_ERROR),
    reportDate: z.string().nonempty(REQUIRED_FIELD_ERROR),
    status: z.nativeEnum(ScopeOfWorkStatus),
    project: ProjectSchema,
    description: z.string().nonempty(REQUIRED_FIELD_ERROR),
    regions: z.array(RegionSchema).nullish(),
    owner: UserSchema,
    weldingSpecialistId: z.string().nonempty(REQUIRED_FIELD_ERROR),
    commonScopeOfWorkId: z.string().nullish(),
    contractorId: z.string().nullish(),
    constructionCode: z.string().nullish(),
    wpsCode: z.string().nullish(),
    service: z.string().nullish(),
    materials: z.string().nullish(),
    thickestGrooveWeld: z.string().nullish(),
    smallestGrooveWeld: z.string().nullish(),
    continuityType: z.string().nullish(),
    inServiceWelding: z.boolean(),
    allowQualificationsByNde: z.boolean(),
    applyRepairWelderQualReqs: z.boolean(),
    newYorkProject: z.boolean(),
    welders: z.array(WelderSchema).nullish(),
  })
  .transform((data) => data as ScopeOfWork)
  .superRefine(async (data, ctx) => {
    await runAPIValidation(data, ctx);

    if (data.status > ScopeOfWorkStatus.Partial) {
      runExtendedValidation(data, ctx);
    }
  });

async function runAPIValidation(data: ScopeOfWork, ctx: z.RefinementCtx) {
  const result = await api.validateScopeOfWork({
    ...data,
    // API throws error with empty project.
    project: data.project?.name ? data.project : undefined,
  });

  if (!result.isDescriptionUnique) {
    ctx.addIssue({
      path: ["description"],
      code: z.ZodIssueCode.custom,
      message: "Description must be unique",
    });
  }
}

function runExtendedValidation(data: ScopeOfWork, ctx: z.RefinementCtx) {
  if (!data.regions?.length) {
    ctx.addIssue(requiredFieldError("regions"));
  }
  if (!data.constructionCode) {
    ctx.addIssue(requiredFieldError("constructionCode"));
  }
  if (!data.wpsCode) {
    ctx.addIssue(requiredFieldError("wpsCode"));
  }
  if (!data.service) {
    ctx.addIssue(requiredFieldError("service"));
  }
  if (!data.materials) {
    ctx.addIssue(requiredFieldError("materials"));
  }
  if (!data.thickestGrooveWeld) {
    ctx.addIssue(requiredFieldError("thickestGrooveWeld"));
  }
  if (!data.smallestGrooveWeld) {
    ctx.addIssue(requiredFieldError("smallestGrooveWeld"));
  }
  if (!data.continuityType) {
    ctx.addIssue(requiredFieldError("continuityType"));
  }
}

function requiredFieldError(field: keyof ScopeOfWork): z.IssueData {
  return {
    path: [field],
    code: z.ZodIssueCode.custom,
    message: REQUIRED_FIELD_ERROR,
  };
}
